import { Box } from '@mui/material';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
} from '@peritasai/ui-components';
import { useTranslation } from 'react-i18next';
import {
  VERIFY_ASSETS_CONFIRMED_CART_COLOR,
  VERIFY_ASSETS_SUCCESS_COLOR,
} from '../colors';
import { AssetItemRow, AssetItemState } from './AssetItemRow';
import { DetectedCheckIcon } from './DetectedCheckIcon';

export interface CategoryCardItem {
  id: string;
  name: string;
  assetId?: string;
  state: AssetItemState;
}

export interface CategoryCardProps {
  titleKey: string;
  items: CategoryCardItem[];
  detectedCount: number;
  totalCount: number;
  /** Trays only: the case's selected cart name, when applicable. */
  cartName?: string;
  /** Trays only: the selected cart is detected in the case's OR room. */
  isCartInRoom?: boolean;
  /** Case assets have been confirmed (verifiedAt set). */
  isVerified?: boolean;
}

function cartCheckColor(isVerified: boolean, isCartInRoom: boolean): string {
  if (isVerified) return VERIFY_ASSETS_CONFIRMED_CART_COLOR;
  return isCartInRoom ? VERIFY_ASSETS_SUCCESS_COLOR : 'action.disabled';
}

export function CategoryCard({
  titleKey,
  items,
  detectedCount,
  totalCount,
  cartName,
  isCartInRoom = false,
  isVerified = false,
}: CategoryCardProps) {
  const { t } = useTranslation(['periverse']);

  const countLabel = cartName
    ? t('pages.caseVerifyAssets.itemCountWithCart', {
        cartName,
        count: detectedCount,
        total: totalCount,
      })
    : t('pages.caseVerifyAssets.itemCount', {
        count: detectedCount,
        total: totalCount,
      });

  return (
    <Card
      elevation={0}
      sx={{ borderRadius: '8px' }}
      data-testid={`category-card-${titleKey}`}
    >
      <CardHeader
        title={
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.334 }}>
            {t(titleKey)}
          </Typography>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {cartName && (
              <DetectedCheckIcon
                data-testid="category-card-cart-check"
                sx={{
                  fontSize: 24,
                  color: cartCheckColor(isVerified, isCartInRoom),
                }}
              />
            )}
            <Typography
              variant="overline"
              sx={{
                color: 'text.primary',
                letterSpacing: '1px',
                lineHeight: 1.6,
              }}
            >
              {countLabel}
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'center', '& .MuiCardHeader-action': { m: 0 } }}
      />
      <CardContent sx={{ pt: 0, '&:last-child': { pb: '16px' } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {items.map((item) => (
            <AssetItemRow
              key={item.id}
              name={item.name}
              state={item.state}
              assetId={item.assetId}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
